import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Logo from '../components/brand/Logo';
import { getAuthErrorMessage } from '../utils/authErrors';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/home');
    } catch (error) {
      setError(getAuthErrorMessage(error, t));
    }

    setLoading(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-accent-primary/10 via-bg-primary to-accent-secondary/10 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent-primary/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent-secondary/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Logo to="/" size={44} />
        </div>

        {/* Login Card */}
        <div className="glass-morphism rounded-3xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-text-primary mb-2">
              {t('auth.welcomeBack')}
            </h2>
            <p className="text-text-secondary">
              {t('auth.signInPrompt')}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-red-500 dark:text-red-400" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-4">
              <Input
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('auth.enterEmail')}
                className="input-focus bg-bg-primary/50 backdrop-blur-sm"
                required
              />

              <Input
                label={t('auth.password')}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('auth.enterPassword')}
                className="input-focus bg-bg-primary/50 backdrop-blur-sm"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-gradient text-white border-0 py-4 text-lg font-semibold"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>{t('auth.signingIn')}</span>
                </div>
              ) : (
                t('auth.loginButton')
              )}
            </Button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-text-secondary">
              {t('auth.noAccount')}{' '}
              <Link
                to="/register"
                className="font-semibold text-brand-primary hover:text-brand-dark transition-colors"
              >
                {t('auth.signUpLink')}
              </Link>
            </p>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-8 text-center">
          <p className="text-sm text-text-tertiary">
            {t('auth.secureLoginInfo')}
          </p>
        </div>
      </div>
    </div>
  );
} 